import { useState } from "react";
import { Activity, Trash2 } from "lucide-react";
import { useEventStream, type FeedEvent } from "@/api/hooks/useEventStream";
import { SectionCard } from "@/components/SectionCard";
import { StatusDot } from "@/components/StatusDot";
import { relativeTime } from "@/lib/relativeTime";
import { cn } from "@/lib/utils";

const CATEGORIES = [
  { label: "Standard (lifecycle + peer)", topics: ["agent.*.lifecycle.>", "master.peer.*.event"] },
  { label: "Nur Lifecycle", topics: ["agent.*.lifecycle.>"] },
  { label: "Nur Peer-Events", topics: ["master.peer.*.event"] },
  { label: "+ Channel-Inbound", topics: ["agent.*.lifecycle.>", "master.peer.*.event", "channel.*.inbound.msg"] },
  { label: "+ Heartbeats", topics: ["agent.*.lifecycle.>", "master.peer.*.event", "node.*.heartbeat", "channel.*.heartbeat"] },
] as const;

type Tone = "ok" | "info" | "warn" | "danger" | "muted";

// #design-pass W3: echtes SSE-Event → Design-Anzeige (level/topic/agentId/text).
function parseEvent(e: FeedEvent): { topic: string; agentId: string | null; level: Tone; text: string } {
  const parts = e.subject.split(".");
  let agentId: string | null = null;
  let topic = parts[0] ?? "event";
  if (parts[0] === "agent" && parts.length >= 3) {
    agentId = parts[1];
    topic = parts[2];
  } else if (parts[0] === "master" && parts[1] === "peer") {
    topic = "peer";
  } else if (parts[0] === "channel" || parts[0] === "node") {
    topic = parts[2] ?? parts[0];
  }
  const s = e.subject.toLowerCase();
  const level: Tone = /crash|error|fail|dead|revok/.test(s)
    ? "danger"
    : /degrad|warn|suspend|drain/.test(s)
      ? "warn"
      : /running|started|online|available|connected|ready/.test(s)
        ? "ok"
        : "info";
  let text = e.subject;
  if (e.payload && typeof e.payload === "object") {
    const p = e.payload as Record<string, unknown>;
    text = String(p.message ?? p.event ?? p.kind ?? p.status ?? p.text ?? e.subject);
  } else if (typeof e.payload === "string" && e.payload) {
    text = e.payload;
  }
  return { topic: topic.toUpperCase(), agentId, level, text };
}

const statusTone: Record<string, Tone> = { open: "ok", connecting: "warn", error: "warn", closed: "muted" };
const statusLabel: Record<string, string> = { open: "Live", connecting: "verbindet…", error: "reconnect", closed: "offline" };

export function ActivityFeed() {
  const [categoryIdx, setCategoryIdx] = useState(0);
  const category = CATEGORIES[categoryIdx];
  const stream = useEventStream({ topics: category.topics, bufferSize: 100 });

  return (
    <SectionCard
      testId="activity-feed"
      icon={<Activity className="h-[18px] w-[18px]" />}
      title="Live-Aktivität"
      description="Echtzeit-Ereignisse (SSE)"
      noPad
      action={
        <div className="flex items-center gap-2">
          <span
            className={cn(
              "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold",
              stream.status === "open" ? "border-ok-border bg-ok-bg text-ok" : "border-warn-border bg-warn-bg text-warn",
            )}
            data-testid="feed-status"
          >
            <StatusDot tone={statusTone[stream.status] ?? "muted"} pulse={stream.status === "open"} />
            {statusLabel[stream.status] ?? stream.status}
          </span>
          <select
            value={categoryIdx}
            onChange={(e) => setCategoryIdx(Number(e.target.value))}
            data-testid="feed-category-select"
            className="h-[30px] rounded-sm border border-border bg-surface2 px-2 text-xs text-text"
          >
            {CATEGORIES.map((cat, i) => (
              <option key={i} value={i}>
                {cat.label}
              </option>
            ))}
          </select>
          <button
            onClick={stream.clear}
            disabled={stream.events.length === 0}
            aria-label="Clear feed"
            data-testid="feed-clear"
            className="grid h-[30px] w-[30px] place-items-center rounded-sm text-text3 transition-colors hover:bg-surface3 hover:text-text disabled:opacity-40"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      }
    >
      {stream.events.length === 0 ? (
        <div className="px-[18px] py-12 text-center text-sm text-text3" data-testid="feed-empty">
          {stream.status === "open" ? "Verbunden — warte auf Events…" : `Kein Stream aktiv (${statusLabel[stream.status] ?? stream.status}).`}
        </div>
      ) : (
        <div className="max-h-[340px] overflow-y-auto" data-testid="feed-list">
          {stream.events.map((evt, i) => {
            const p = parseEvent(evt);
            return (
              <div key={evt.key} className={cn("flex gap-3 px-[18px] py-[11px]", i < stream.events.length - 1 && "border-b border-border")}>
                <div className="pt-[5px]">
                  <StatusDot tone={p.level} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[13px] text-text">{p.text}</div>
                  <div className="mt-0.5 flex items-center gap-2">
                    <span className="text-[10.5px] font-bold uppercase tracking-wider text-text3">{p.topic}</span>
                    {p.agentId && <span className="mono text-[11px] text-text3">{p.agentId}</span>}
                    <span className="ml-auto text-[11.5px] text-text3">{relativeTime(evt.ts)}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </SectionCard>
  );
}
